import type { HikingDifficulty, MtbTrailDifficulty } from "~/lib/api";
import { cn } from "~/lib/utils";

type TrailRatingSymbol = "circle" | "square" | "diamond" | "double-diamond" | "double-diamond-orange";

const HIKING_SYMBOL: Record<HikingDifficulty, TrailRatingSymbol> = {
  easy: "circle",
  moderate: "square",
  hard: "diamond",
  expert: "double-diamond",
};

const MTB_SYMBOL: Record<MtbTrailDifficulty, TrailRatingSymbol> = {
  green: "circle",
  blue: "square",
  black: "diamond",
  "double-black": "double-diamond",
  proline: "double-diamond-orange",
};

interface DifficultyIconProps {
  difficulty: HikingDifficulty;
  outline?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function DifficultyIcon({ difficulty, outline, className, "aria-label": ariaLabel }: DifficultyIconProps) {
  return (
    <TrailRatingIcon
      symbol={HIKING_SYMBOL[difficulty]}
      outline={outline}
      className={className}
      aria-label={ariaLabel}
    />
  );
}

interface MtbDifficultyIconProps {
  difficulty: MtbTrailDifficulty;
  outline?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function MtbDifficultyIcon({ difficulty, outline, className, "aria-label": ariaLabel }: MtbDifficultyIconProps) {
  return (
    <TrailRatingIcon symbol={MTB_SYMBOL[difficulty]} outline={outline} className={className} aria-label={ariaLabel} />
  );
}

interface TrailRatingIconProps {
  symbol: TrailRatingSymbol;
  outline?: boolean;
  className?: string;
  "aria-label"?: string;
}

function TrailRatingIcon({ symbol, outline, className, "aria-label": ariaLabel }: TrailRatingIconProps) {
  const a11y = ariaLabel ? { role: "img" as const, "aria-label": ariaLabel } : { "aria-hidden": true as const };
  switch (symbol) {
    case "circle":
      return (
        <svg viewBox="0 0 16 16" className={cn("size-3 shrink-0", className)} {...a11y}>
          {outline && <circle cx="8" cy="8" r="6" fill="currentColor" />}
          <circle cx="8" cy="8" r="5" fill="#16a34a" />
        </svg>
      );
    case "square":
      return (
        <svg viewBox="0 0 16 16" className={cn("size-3 shrink-0", className)} {...a11y}>
          {outline && <rect x="2" y="2" width="12" height="12" fill="currentColor" />}
          <rect x="3" y="3" width="10" height="10" fill="#2563eb" />
        </svg>
      );
    case "diamond":
      return (
        <svg viewBox="0 0 16 16" className={cn("size-3 shrink-0", className)} {...a11y}>
          {outline && <polygon points="8,0 16,8 8,16 0,8" fill="currentColor" />}
          <polygon points="8,2 14,8 8,14 2,8" fill="#111827" />
        </svg>
      );
    case "double-diamond":
      return (
        <svg viewBox="0 0 22 16" className={cn("h-3 w-auto shrink-0", className)} {...a11y}>
          {outline && (
            <>
              <polygon points="8,-1 17,8 8,17 -1,8" fill="currentColor" />
              <polygon points="14,-1 23,8 14,17 5,8" fill="currentColor" />
            </>
          )}
          <polygon points="8,1 15,8 8,15 1,8" fill="#111827" />
          <polygon points="14,1 21,8 14,15 7,8" fill="#111827" />
        </svg>
      );
    case "double-diamond-orange":
      return (
        <svg viewBox="0 0 22 16" className={cn("h-3 w-auto shrink-0", className)} {...a11y}>
          {outline && (
            <>
              <polygon points="8,-1 17,8 8,17 -1,8" fill="currentColor" />
              <polygon points="14,-1 23,8 14,17 5,8" fill="currentColor" />
            </>
          )}
          <polygon points="8,1 15,8 8,15 1,8" fill="#ea580c" />
          <polygon points="14,1 21,8 14,15 7,8" fill="#ea580c" />
        </svg>
      );
  }
}
